import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { theme } from "../../theme"; 
import { 
    Box, Button, Container, Table, TableBody, TableCell, TableHead, TableRow, 
    Grid, Card, CardContent, Typography, Chip, Divider, TableContainer, MenuItem, Select, CircularProgress
} from "@mui/material";
import { AccessTime, AnalyticsOutlined, RefreshOutlined, GroupOutlined, EmojiEventsOutlined, PanToolOutlined, SmartToyOutlined } from "@mui/icons-material";
import axios from "axios";
import { useEffect, useState } from "react";
import swal from "sweetalert";
import { t } from "i18next";

// ==========================================
// THEME GIAO DIỆN METRONIC CHO ADMIN
// ==========================================
const metronicTheme = createTheme({
    palette: {
        background: { default: "#F5F8FA", paper: "#FFFFFF" }, 
        text: { primary: "#181C32", secondary: "#7E8299" }, 
        primary: { main: "#1B84FF", light: "#F1FAFF" },
        success: { main: "#50CD89", light: "#E8FFF3" },
        danger: { main: "#F1416C", light: "#FFF5F8" },
        warning: { main: "#FFC700", light: "#FFF8DD" },
        info: { main: "#7239EA", light: "#F8F5FF" }
    },
    typography: { 
        fontFamily: "'Plus Jakarta Sans', sans-serif" 
    },
    components: {
        MuiCard: { styleOverrides: { root: { borderRadius: "12px", boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.03)", border: "none" } } },
        MuiTableCell: { styleOverrides: { root: { borderBottom: "1px dashed #EFF2F5 !important", padding: "16px 12px", color: "#3F4254", fontSize: "0.85rem", fontFamily: "'Plus Jakarta Sans', sans-serif !important" } } },
        MuiButton: { styleOverrides: { root: { fontFamily: "'Plus Jakarta Sans', sans-serif", textTransform: 'none', borderRadius: '8px' } } },
        MuiSelect: { styleOverrides: { select: { padding: '8px 12px' } } },
        MuiTable: { styleOverrides: { root: { display: 'table', width: '100%', borderCollapse: 'separate', borderSpacing: 0 } } }
    }
});

axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem("user");
        if (token) config.headers["Authorization"] = `Bearer ${token}`;
        return config;
    },
    (error) => Promise.reject(error)
);

// DỮ LIỆU BỘ BÀI VÀ HÀM BỔ TRỢ
const RANKS = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const SUITS = [
    { value: 'Hearts', label: 'Cơ (♥)' },
    { value: 'Diamonds', label: 'Rô (♦)' },
    { value: 'Clubs', label: 'Chuồn (♣)' },
    { value: 'Spades', label: 'Bích (♠)' }
];

const suitMap = {
    'Hearts': { icon: '♥', color: '#F1416C' },
    'Diamonds': { icon: '♦', color: '#F1416C' },
    'Clubs': { icon: '♣', color: '#181C32' },
    'Spades': { icon: '♠', color: '#181C32' }
};

const getVal = (r) => (['10','J','Q','K'].includes(r) ? 0 : (r === 'A' ? 1 : parseInt(r)));

// Hàm cắt bỏ tiền tố sảnh (VD: "2_0014" -> "0014")
const formatId = (idData) => {
    if (!idData) return "---";
    const rawId = typeof idData === 'object' ? idData.id_bet : idData;
    const strId = String(rawId);
    return strId.includes('_') ? strId.split('_').pop() : strId;
};

// DANH SÁCH 16 CỬA CƯỢC CÓ TRONG GAME
const BET_OPTIONS = [
    { id: "1", label: "CON", color: "blue" },
    { id: "2", label: "CÁI", color: "red" },
    { id: "3", label: "HÒA", color: "green" },
    { id: "4", label: "SUPER 6", color: "red" },
    { id: "5", label: "CON ĐÔI", color: "blue" },
    { id: "6", label: "CÁI ĐÔI", color: "red" },
    { id: "7", label: "ĐÔI BẤT KỲ", color: "green" },
    { id: "8", label: "ĐÔI HOÀN MỸ", color: "green" },
    { id: "9", label: "TÀI", color: "green" },
    { id: "10", label: "XỈU", color: "green" },
    { id: "11", label: "CON LẺ", color: "blue" },
    { id: "12", label: "CON CHẴN", color: "blue" },
    { id: "13", label: "CÁI LẺ", color: "red" },
    { id: "14", label: "CÁI CHẴN", color: "red" },
    { id: "17", label: "CON LONG BẢO", color: "blue" },
    { id: "18", label: "CÁI LONG BẢO", color: "red" }
];

// MÀU SẮC CHO CÁC Ô CƯỢC
const getColorTheme = (color, isActive) => {
    if (!isActive) return { 
        bg: '#F5F8FA', 
        border: '#E4E6EF', 
        text: '#7E8299', 
        value: '#181C32', 
        shadow: 'none' 
    };
    if (color === 'blue') return { bg: '#F1FAFF', border: '#1B84FF', text: '#1B84FF', value: '#1B84FF', shadow: '0 0 15px rgba(27, 132, 255, 0.4)' };
    if (color === 'red') return { bg: '#FFF5F8', border: '#F1416C', text: '#F1416C', value: '#F1416C', shadow: '0 0 15px rgba(241, 65, 108, 0.4)' };
    if (color === 'green') return { bg: '#E8FFF3', border: '#50CD89', text: '#50CD89', value: '#50CD89', shadow: '0 0 15px rgba(80, 205, 137, 0.4)' };
};

// COMPONENT HIỂN THỊ LÁ BÀI BẰNG CSS
const CardDisplay = ({ card }) => {
    if (!card || !card.rank || !card.suit) {
        return (
            <Box sx={{ width: 32, height: 46, backgroundColor: '#EFF2F5', borderRadius: 1, border: '1px solid #E4E6EF' }} />
        );
    }
    const s = suitMap[card.suit];
    return (
        <Box sx={{ width: 32, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#FFFFFF', border: '1px solid #E4E6EF', borderRadius: 1, fontWeight: '900', fontSize: '14px', color: s?.color, boxShadow: '0px 2px 4px rgba(0,0,0,0.05)' }}>
            {card.rank}{s?.icon}
        </Box>
    );
};

// COMPONENT CARD SELECT UI
const CardSelectUI = ({ label, card, setCard, isOptional, disabled }) => (
    <Box mb={2.5}>
        <Typography variant="caption" fontWeight={800} color="#7E8299" mb={1} display="block">{label}</Typography>
        <Box display="flex" gap={1} alignItems="center">
            {isOptional && !card ? (
                <Button disabled={disabled} size="small" variant="outlined" sx={{ width: '100%', py: 1, color: '#A1A5B7', borderColor: '#E4E6EF', borderStyle: 'dashed' }} onClick={() => setCard({ suit: 'Spades', rank: 'A' })}>
                    + Thêm lá thứ 3
                </Button>
            ) : (
                <>
                    <Select disabled={disabled} size="small" value={card.rank} onChange={e => setCard({...card, rank: e.target.value})} sx={{ minWidth: 70, fontWeight: 800, backgroundColor: '#FFF' }}>
                        {RANKS.map(r => <MenuItem key={r} value={r} sx={{fontWeight: 700}}>{r}</MenuItem>)}
                    </Select>
                    <Select disabled={disabled} size="small" value={card.suit} onChange={e => setCard({...card, suit: e.target.value})} sx={{ flexGrow: 1, minWidth: 120, fontWeight: 800, backgroundColor: '#FFF' }}>
                        {SUITS.map(s => <MenuItem key={s.value} value={s.value} sx={{fontWeight: 700}}>{s.label}</MenuItem>)}
                    </Select>
                    {isOptional && (
                        <Typography 
                            component="span" 
                            color="error" 
                            sx={{ cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, fontSize: '13px', fontWeight: 700, ml: 1, "&:hover": { textDecoration: disabled ? 'none' : 'underline' } }} 
                            onClick={() => !disabled && setCard(null)}
                        >
                            Xóa
                        </Typography>
                    )}
                </>
            )}
        </Box>
    </Box>
);

// COMPONENT CHÍNH
function SetBcr() {
    // 🟢 SẢNH ĐANG CHỌN (Mặc định sảnh 1)
    const [currentTableId, setCurrentTableId] = useState(1);

    const [list30, setList30] = useState([]);
    const [bet, setBet] = useState(null);

    const [second, setSecond] = useState(60);
    const [start, setStart] = useState(false);
    const [dulieunhap, setDulieunhap] = useState(new Date());
    const [update, setUpdate] = useState(0);
    const [current, setCurrent] = useState(null);

    // STATE LÁ BÀI THỦ CÔNG
    const [pCard1, setPCard1] = useState({ suit: 'Hearts', rank: 'A' });
    const [pCard2, setPCard2] = useState({ suit: 'Hearts', rank: 'A' });
    const [pCard3, setPCard3] = useState(null);

    const [bCard1, setBCard1] = useState({ suit: 'Spades', rank: 'A' });
    const [bCard2, setBCard2] = useState({ suit: 'Spades', rank: 'A' });
    const [bCard3, setBCard3] = useState(null);

    const [currentEditId, setCurrentEditId] = useState(null);
    const date = new Date();

    const isLocked = second <= 0 || !start; 
    const isDangerTime = second <= 15; 

    // BIẾN KIỂM TRA XEM CÓ KHÁCH CƯỢC HAY KHÔNG
    const hasBets = current && current.length > 0;

    function formatDate(m) {
        if (!m) return "";
        const d = new Date(m);
        return `${("0" + d.getHours()).slice(-2)}:${("0" + d.getMinutes()).slice(-2)} ${("0" + d.getDate()).slice(-2)}/${("0" + (d.getMonth() + 1)).slice(-2)}`;
    }

    const getBetLabel = (stateStr) => {
        if (!stateStr) return "";
        const map = { 
            "1": "CON", "2": "CÁI", "3": "HÒA", "4": "SUPER 6", 
            "5": "CON ĐÔI", "6": "CÁI ĐÔI", "7": "ĐÔI BẤT KỲ", "8": "ĐÔI HOÀN MỸ", 
            "9": "TÀI", "10": "XỈU", "11": "CON LẺ", "12": "CON CHẴN", 
            "13": "CÁI LẺ", "14": "CÁI CHẴN", "17": "CON LONG BẢO", "18": "CÁI LONG BẢO"
        };
        const states = String(stateStr).split(" ");
        return states.map(s => map[s] || s).join(" - ");
    };

    // Hàm tự động set bộ bài ban đầu
    useEffect(() => {
        if (bet && bet.id_bet !== currentEditId) {
            setCurrentEditId(bet.id_bet);
            try {
                let targetResultStr = bet.result || bet.ai_result;
                if (targetResultStr) {
                    const parsed = JSON.parse(targetResultStr);
                    if (parsed.playerHand && parsed.bankerHand) {
                        setPCard1(parsed.playerHand[0] || { suit: 'Hearts', rank: 'A' });
                        setPCard2(parsed.playerHand[1] || { suit: 'Hearts', rank: 'A' });
                        setPCard3(parsed.playerHand[2] || null);

                        setBCard1(parsed.bankerHand[0] || { suit: 'Spades', rank: 'A' });
                        setBCard2(parsed.bankerHand[1] || { suit: 'Spades', rank: 'A' });
                        setBCard3(parsed.bankerHand[2] || null);
                    }
                } else {
                    setPCard1({ suit: 'Hearts', rank: 'A' });
                    setPCard2({ suit: 'Hearts', rank: 'A' });
                    setPCard3(null);
                    setBCard1({ suit: 'Spades', rank: 'A' });
                    setBCard2({ suit: 'Spades', rank: 'A' });
                    setBCard3(null);
                }
            } catch (e) {
                console.error("Lỗi parse bài", e);
            }
        }
    }, [bet, currentEditId]);

    // LẤY DỮ LIỆU ĐỘNG THEO SẢNH (TABLE_ID)
    const reloadHistorySilently = () => {
        axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/getadmin`)
            .then((res) => { if(res.data?.data?.[0]) setBet(res.data.data[0]); }).catch(() => {});
        axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/list30bet`).then((res) => setList30(res.data.data)).catch(() => {});
    };

    // KHI ĐỔI SẢNH HOẶC LOAD LẦN ĐẦU -> GỌI API THEO SẢNH
    useEffect(() => {
        // Reset trước khi đổi
        setBet(null);
        setStart(false);
        setSecond(60);
        setCurrent(null);
        setList30([]);

        axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/getadmin`).then((res) => {
            if(res.data?.data?.[0]) {
                setBet(res.data.data[0]);
                setDulieunhap(new Date(res.data.data[0].createdAt));
                setStart(true);
            }
        }).catch(() => {});

        axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/list30bet`).then((res) => setList30(res.data.data)).catch(() => {});
        axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/getcurrent`).then((res) => setCurrent(res.data.data)).catch(() => {});
    }, [currentTableId]); // <--- RENDER LẠI KHI ĐỔI SẢNH

    useEffect(() => {
        const timer = setInterval(() => {
            if (Math.floor(60 - (new Date() - dulieunhap) / 1000) < 0) {
                axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/getadmin`).then((res) => {
                    if(res.data?.data?.[0]) {
                        setBet(res.data.data[0]);
                        setDulieunhap(new Date(res.data.data[0].createdAt));
                    }
                }).catch(() => {});
                axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/list30bet`).then((res) => setList30(res.data.data)).catch(() => {});
                axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/getcurrent`).then((res) => setCurrent(res.data.data)).catch(() => {});
            }
        }, 500);
        return () => clearInterval(timer);
    }, [dulieunhap, currentTableId]);

    useEffect(() => {
        let curTime_second = Math.floor(60 - (date - dulieunhap) / 1000);
        let myTimeout;
        if (curTime_second <= 60 && curTime_second >= 0) {
            setSecond(curTime_second);
            setStart(true);
            return () => clearTimeout(myTimeout);
        } else {
            myTimeout = setTimeout(() => setUpdate(update + 1), 500);
        }
    }, [update, dulieunhap]);

    useEffect(() => {
        let curTime_second = Math.floor(60 - (date - dulieunhap) / 1000);
        let myTimeout = 0;
        if (start) {
            setSecond(curTime_second);
            if (curTime_second > 60 || curTime_second <= 0) {
                setStart(false);
                setSecond(0); 
                return () => clearTimeout(myTimeout);
            }
            myTimeout = setTimeout(() => {
                setSecond(second - 1);
                axios.get(`${process.env.REACT_APP_API_URL}/baccarat/sanh${currentTableId}/getcurrent`).then((res) => setCurrent(res.data.data)).catch(() => setCurrent(null));
            }, 1000);
        }
        return () => clearTimeout(myTimeout);
    }, [second, start, dulieunhap, currentTableId]);

    // HÀM TẠO BỘ BÀI RANDOM CHUẨN XÁC THEO LUẬT BACCARAT
    const handleRandomWin = (desiredWinner) => {
        const suits = ['Hearts', 'Diamonds', 'Clubs', 'Spades'];
        while (true) {
            let deck = [];
            suits.forEach(s => RANKS.forEach(r => deck.push({suit: s, rank: r})));
            deck = deck.sort(() => Math.random() - 0.5);

            const pHand = [deck.pop(), deck.pop()];
            const bHand = [deck.pop(), deck.pop()];

            const score = (hand) => hand.reduce((a,c) => a + getVal(c.rank), 0) % 10;
            let pScore = score(pHand);
            let bScore = score(bHand);

            let pDrawn = false;
            let pThird = null;
            
            // Luật rút lá thứ 3 của Player
            if (pScore <= 5 && bScore < 8 && pScore < 8) {
                pThird = deck.pop();
                pHand.push(pThird);
                pScore = score(pHand);
                pDrawn = true;
            }

            // Luật rút lá thứ 3 của Banker
            if (bScore <= 5 && pScore < 8 && bScore < 8) {
                let bDraws = false;
                if (!pDrawn) {
                    if (bScore <= 5) bDraws = true;
                } else {
                    const p3Val = getVal(pThird.rank);
                    if (bScore <= 2) bDraws = true;
                    else if (bScore === 3 && p3Val !== 8) bDraws = true;
                    else if (bScore === 4 && [2,3,4,5,6,7].includes(p3Val)) bDraws = true;
                    else if (bScore === 5 && [4,5,6,7].includes(p3Val)) bDraws = true;
                    else if (bScore === 6 && [6,7].includes(p3Val)) bDraws = true;
                }
                if (bDraws) {
                    bHand.push(deck.pop());
                    bScore = score(bHand);
                }
            }

            let winner = 3;
            if (pScore > bScore) winner = 1;
            else if (bScore > pScore) winner = 2;

            if (winner === desiredWinner) {
                setPCard1(pHand[0]);
                setPCard2(pHand[1]);
                setPCard3(pHand[2] || null);
                
                setBCard1(bHand[0]);
                setBCard2(bHand[1]);
                setBCard3(bHand[2] || null);
                break;
            }
        }
    };

    const handleRealSubmit = (e) => {
        e.preventDefault();
        if (isLocked) return swal("Cảnh báo", "Đang mở bài, không thể chỉnh sửa kết quả nữa!", "warning");
        if (!bet) return swal("Lỗi", "Không tìm thấy dữ liệu phiên cược!", "error");
        
        const playerHand = [pCard1, pCard2];
        if (pCard3) playerHand.push(pCard3);

        const bankerHand = [bCard1, bCard2];
        if (bCard3) bankerHand.push(bCard3);

        const pScore = currentPScore;
        const bScore = currentBScore;

        let winner = 3;
        if (pScore > bScore) winner = 1;
        else if (bScore > pScore) winner = 2;

        const finalResult = JSON.stringify({ playerHand, bankerHand, playerScore: pScore, bankerScore: bScore, winner });

        const formData = { 
            _id: bet._id, 
            id_bet: bet.id_bet, 
            result: finalResult,
            table: currentTableId // Thêm currentTableId để server update đúng sảnh
        };
        
        axios.post(`${process.env.REACT_APP_API_URL}/baccarat/update`, formData)
            .then((res) => {
                if(res.data && res.data.success) {
                    swal("Thành công", `Đã chốt kết quả phiên ${formatId(bet.id_bet)} thành công!`, "success");
                    reloadHistorySilently();
                } else {
                    swal("Lỗi", res.data?.message || "Lỗi xử lý từ server", "error");
                }
            })
            .catch((err) => {
                const errorMsg = err.response?.data?.message || "Không thể kết nối đến máy chủ, có lỗi xảy ra!";
                swal("Lỗi", errorMsg, "error");
            });
    };

    function getRawTotal(numStr) {
        if (!current) return 0;
        let totalMoney = 0;
        current.forEach((item) => {
            const states = String(item.state).split(" ");
            if (states.includes(numStr)) { totalMoney += Number(item.money); }
        });
        return totalMoney;
    }

    function thongke(numStr) {
        return getRawTotal(numStr).toLocaleString("vi-VN");
    }

    // =========================================================================
    // THUẬT TOÁN TÍNH TOÁN KẾT QUẢ THỜI GIAN THỰC
    // =========================================================================
    const rtPlayerHand = [pCard1, pCard2, pCard3].filter(c => c !== null);
    const rtBankerHand = [bCard1, bCard2, bCard3].filter(c => c !== null);

    const currentPScore = rtPlayerHand.reduce((acc, c) => acc + getVal(c.rank), 0) % 10;
    const currentBScore = rtBankerHand.reduce((acc, c) => acc + getVal(c.rank), 0) % 10;
    
    let rtWinnerTxt = "ĐANG HÒA";
    let rtWinnerColor = "#50CD89";
    if (currentPScore > currentBScore) { rtWinnerTxt = "CON ĐANG THẮNG"; rtWinnerColor = "#1B84FF"; }
    else if (currentBScore > currentPScore) { rtWinnerTxt = "CÁI ĐANG THẮNG"; rtWinnerColor = "#F1416C"; }

    const checkActiveWinningStates = () => {
        const winning = [];
        if (rtPlayerHand.length < 2 || rtBankerHand.length < 2) return winning;

        const p2Score = (getVal(rtPlayerHand[0].rank) + getVal(rtPlayerHand[1].rank)) % 10;
        const b2Score = (getVal(rtBankerHand[0].rank) + getVal(rtBankerHand[1].rank)) % 10;
        const isPNatural = p2Score >= 8;
        const isBNatural = b2Score >= 8;

        let winner = 3;
        if (currentPScore > currentBScore) winner = 1;
        else if (currentBScore > currentPScore) winner = 2;

        if (winner === 1) winning.push(1);
        if (winner === 2) { winning.push(2); if (currentBScore === 6) winning.push(4); }
        if (winner === 3) winning.push(3);

        const isPPair = rtPlayerHand[0].rank === rtPlayerHand[1].rank;
        const isBPair = rtBankerHand[0].rank === rtBankerHand[1].rank;
        if (isPPair) winning.push(5);
        if (isBPair) winning.push(6);
        if (isPPair || isBPair) winning.push(7);

        const isPPP = isPPair && rtPlayerHand[0].suit === rtPlayerHand[1].suit;
        const isBPP = isBPair && rtBankerHand[0].suit === rtBankerHand[1].suit;
        if (isPPP || isBPP) winning.push(8);

        const totalCards = rtPlayerHand.length + rtBankerHand.length;
        if (totalCards > 4) winning.push(9);
        if (totalCards === 4) winning.push(10);

        if (currentPScore % 2 !== 0) winning.push(11); else winning.push(12);
        if (currentBScore % 2 !== 0) winning.push(13); else winning.push(14);

        if (winner === 1 && (isPNatural || (currentPScore - currentBScore) >= 4)) winning.push(17);
        if (winner === 2 && (isBNatural || (currentBScore - currentPScore) >= 4)) winning.push(18);

        return winning;
    };

    const activeWinningBets = checkActiveWinningStates();

    // =========================================================================
    // AI PHÂN TÍCH LỢI NHUẬN NHÀ CÁI (HIỂN THỊ THÔNG MINH THEO SỐ LƯỢNG NGƯỜI CƯỢC)
    // =========================================================================
    const calculateProfitScenarios = () => {
        let totalBet = 0;
        let betCon = 0;
        let betCai = 0;
        let betHoa = 0;

        if (hasBets) {
            totalBet = current.reduce((a, b) => a + Number(b.money), 0);
            betCon = getRawTotal("1");
            betCai = getRawTotal("2");
            betHoa = getRawTotal("3");
        }
        
        const profitCon = totalBet - (betCon * 2);
        const profitCai = totalBet - (betCai * 2);
        const profitHoa = totalBet - ((betHoa * 9) + betCon + betCai);

        const scenarios = [
            { name: "CHO CON THẮNG", profit: profitCon },
            { name: "CHO CÁI THẮNG", profit: profitCai },
            { name: "CHO HÒA THẮNG", profit: profitHoa }
        ];

        scenarios.sort((a, b) => b.profit - a.profit);
        return scenarios;
    };
    const profitScenarios = calculateProfitScenarios();

    const renderCardHistory = (resultStr) => {
        if (!resultStr) return <Typography variant="body2" color="text.secondary">Đang chờ hệ thống xử lý...</Typography>;
        try {
            const rs = JSON.parse(resultStr);
            const isPWin = rs.winner === 1;
            const isBWin = rs.winner === 2;
            const isTie = rs.winner === 3;

            return (
                <Box display="flex" alignItems="center" gap={3} sx={{ backgroundColor: '#F9FAFB', p: 1.5, borderRadius: '8px', border: '1px solid #EFF2F5', width: 'fit-content' }}>
                    <Box display="flex" flexDirection="column" alignItems="center" sx={{ opacity: (!isPWin && !isTie) ? 0.6 : 1 }}>
                        <Typography variant="subtitle2" fontWeight="900" color={isPWin ? "#1B84FF" : "#7E8299"} mb={1} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                            {isPWin && <EmojiEventsOutlined fontSize="small"/>} CON ({rs.playerScore || 0})
                        </Typography>
                        <Box display="flex" gap={0.5}>
                            {rs.playerHand?.map((c, i) => <CardDisplay key={i} card={c} />)}
                        </Box>
                    </Box>

                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                        {isTie ? <Chip label="HÒA" size="small" sx={{ backgroundColor: '#50CD89', color: '#FFF', fontWeight: 800, fontSize: '10px', height: '20px' }} /> 
                               : <Typography variant="caption" fontWeight="900" color="#E4E6EF">VS</Typography>}
                    </Box>

                    <Box display="flex" flexDirection="column" alignItems="center" sx={{ opacity: (!isBWin && !isTie) ? 0.6 : 1 }}>
                        <Typography variant="subtitle2" fontWeight="900" color={isBWin ? "#F1416C" : "#7E8299"} mb={1} sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                            {isBWin && <EmojiEventsOutlined fontSize="small"/>} CÁI ({rs.bankerScore || 0})
                        </Typography>
                        <Box display="flex" gap={0.5}>
                            {rs.bankerHand?.map((c, i) => <CardDisplay key={i} card={c} />)}
                        </Box>
                    </Box>
                </Box>
            );
        } catch (e) { return <Typography variant="body2" color="error">Lỗi dữ liệu</Typography>; }
    };

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <ThemeProvider theme={metronicTheme}>
                    <style>{`
                        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                        .safe-bcr-zone * { text-align: left !important; box-sizing: border-box; font-family: 'Plus Jakarta Sans', sans-serif !important; }
                        .css-hncfs0-MuiTable-root, .MuiTable-root {
                            display: table !important;
                            width: 100% !important;
                            border-collapse: separate !important;
                            border-spacing: 0 !important;
                            min-width: 1460px !important;
                        }
                        
                        /* SCROLLBAR TÙY CHỈNH CHO NÚT CHỌN SẢNH */
                        .sanh-scrollbar::-webkit-scrollbar {
                            height: 6px;
                        }
                        .sanh-scrollbar::-webkit-scrollbar-thumb {
                            background-color: #B5B5C3;
                            border-radius: 10px;
                        }
                        .sanh-scrollbar::-webkit-scrollbar-track {
                            background: transparent;
                        }
                    `}</style>

                    <Box className="safe-bcr-zone" component="main" sx={{ flexGrow: 1, py: 4, backgroundColor: 'background.default', minHeight: 'calc(100vh - 70px)' }}>
                        <Container maxWidth="xl">

                            <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                    <Typography variant="h4" fontWeight="800" color="#181C32">QUẢN LÝ BACCARAT</Typography>
                                    <Typography variant="body2" color="text.secondary">Thống kê cửa cược, phân tích lợi nhuận và điều hướng kết quả</Typography>
                                </Box>
                                <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={() => window.location.reload()} sx={{ borderRadius: '8px', fontWeight: 700 }}>Làm mới</Button>
                            </Box>

                            {/* 🟢 NÚT CHỌN SẢNH BACCARAT 1 -> 6 */}
                            <Box className="sanh-scrollbar" display="flex" gap={1.5} mb={4} overflow="auto" pb={1} sx={{ '&::-webkit-scrollbar': { height: '6px' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#B5B5C3', borderRadius: '10px' } }}>
                                {[1, 2, 3, 4, 5, 6].map((id) => (
                                    <Button 
                                        key={id} 
                                        variant={currentTableId === id ? "contained" : "outlined"}
                                        color={currentTableId === id ? "primary" : "inherit"}
                                        onClick={() => setCurrentTableId(id)}
                                        sx={{ 
                                            fontWeight: '800', 
                                            minWidth: '130px', 
                                            borderRadius: '8px',
                                            borderColor: currentTableId === id ? '#1B84FF' : '#E4E6EF',
                                            color: currentTableId === id ? '#FFF' : '#7E8299',
                                            boxShadow: currentTableId === id ? '0px 4px 12px rgba(27, 132, 255, 0.3)' : 'none',
                                            backgroundColor: currentTableId === id ? '#1B84FF' : '#FFFFFF',
                                            '&:hover': {
                                                backgroundColor: currentTableId === id ? '#005edb' : '#F5F8FA',
                                                borderColor: currentTableId === id ? '#005edb' : '#E4E6EF'
                                            }
                                        }}
                                    >
                                        Bàn Sảnh {id}
                                    </Button>
                                ))}
                            </Box>

                            <Grid container spacing={3} mb={4}>
                                {/* PANEL TRÁI: PHÂN TÍCH LỢI NHUẬN VÀ 16 CỬA CƯỢC THỜI GIAN THỰC */}
                                <Grid item xs={12} lg={6}>
                                    <Card sx={{ height: '100%', border: '1px solid #E4E6EF' }}>
                                        <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                                <Box>
                                                    <Typography variant="caption" fontWeight="800" color="#A1A5B7" sx={{ textTransform: 'uppercase' }}>Kỳ quay hiện tại (Bàn {currentTableId})</Typography>
                                                    {bet ? <Typography variant="h5" fontWeight="800" color="#1B84FF">#{formatId(bet.id_bet)}</Typography> : <Typography variant="h6" fontWeight="700" color="text.secondary">{t('waiting')}...</Typography>}
                                                </Box>
                                                <Chip 
                                                    icon={<AccessTime sx={{ fontSize: '16px !important', color: (isDangerTime && !isLocked) ? '#FFF !important' : 'inherit' }} />} 
                                                    label={isLocked ? "ĐANG TRẢ THƯỞNG" : `00:${second < 10 ? '0' : ''}${second}`} 
                                                    sx={{ 
                                                        backgroundColor: isLocked ? '#7E8299' : (isDangerTime ? '#F1416C' : '#36db15'), 
                                                        color: '#FFFFFF', fontWeight: 800, fontSize: '1.5rem', py: 3, px: 2, borderRadius: '12px',
                                                        animation: (isDangerTime && !isLocked) ? 'pulse 1s infinite' : 'none',
                                                        '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } }
                                                    }} 
                                                />
                                            </Box>
                                            
                                            <Divider sx={{ mb: 3 }} />
                                            
                                            {/* AI PHÂN TÍCH LỢI NHUẬN NHÀ CÁI (HIỂN THỊ THÔNG MINH) */}
                                            <Box mb={3} p={2} sx={{ backgroundColor: '#FFF8DD', border: '1px dashed #FFC700', borderRadius: '12px', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                <Typography variant="subtitle2" fontWeight="900" color="#D97706" mb={(!hasBets || isLocked) ? 0 : 1.5} sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: (!hasBets || isLocked) ? 'center' : 'flex-start' }}>
                                                    <SmartToyOutlined fontSize="small" /> AI TỰ ĐỘNG PHÂN TÍCH LỢI NHUẬN NHÀ CÁI
                                                </Typography>
                                                
                                                {!hasBets ? (
                                                    <Box display="flex" flexDirection="column" alignItems="center" mt={1}>
                                                        <Typography variant="caption" fontWeight="800" color="#D97706" opacity={0.8}>Chưa có người chơi cược. Vui lòng chờ người chơi đặt cược...</Typography>
                                                    </Box>
                                                ) : isLocked ? (
                                                    <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
                                                        <CircularProgress size={24} sx={{ color: '#D97706', mb: 1.5 }} />
                                                        <Typography variant="caption" fontWeight="800" color="#D97706">Hệ thống đang mở bài và chờ phiên cược mới...</Typography>
                                                    </Box>
                                                ) : (
                                                    <Grid container spacing={1.5}>
                                                        {profitScenarios.map((sc, idx) => (
                                                            <Grid item xs={4} key={idx}>
                                                                <Box sx={{ 
                                                                    p: 1.5, 
                                                                    borderRadius: '8px', 
                                                                    backgroundColor: idx === 0 ? '#50CD89' : '#FFFFFF', 
                                                                    color: idx === 0 ? '#FFF' : '#3F4254', 
                                                                    border: idx === 0 ? 'none' : '1px solid #E4E6EF', 
                                                                    textAlign: 'center',
                                                                    boxShadow: idx === 0 ? '0px 4px 10px rgba(80, 205, 137, 0.4)' : 'none'
                                                                }}>
                                                                    <Typography variant="caption" fontWeight="800" display="block">{sc.name}</Typography>
                                                                    <Typography variant="body2" fontWeight="900" mt={0.5}>
                                                                        {sc.profit > 0 ? '+' : ''}{sc.profit.toLocaleString("vi-VN")} đ
                                                                    </Typography>
                                                                </Box>
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                )}
                                            </Box>

                                            <Typography variant="subtitle1" fontWeight="800" color="#181C32" mb={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AnalyticsOutlined color="primary" /> Thông số 16 cửa cược (Bàn {currentTableId})
                                            </Typography>
                                            
                                            <Grid container spacing={1.5}>
                                                {BET_OPTIONS.map((item) => {
                                                    const isActive = activeWinningBets.includes(Number(item.id));
                                                    const style = getColorTheme(item.color, isActive);
                                                    
                                                    return (
                                                        <Grid item xs={6} sm={4} md={3} key={item.id}>
                                                            <Box sx={{ 
                                                                p: 1.5, 
                                                                borderRadius: '10px', 
                                                                backgroundColor: style.bg, 
                                                                border: `1px solid ${style.border}`,
                                                                boxShadow: style.shadow,
                                                                transition: 'all 0.3s ease',
                                                                textAlign: 'center'
                                                            }}>
                                                                <Typography variant="caption" fontWeight="800" color={style.text} display="block" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                    {item.label}
                                                                </Typography>
                                                                <Typography variant="body2" fontWeight="900" color={style.value} sx={{ mt: 0.5 }}>
                                                                    {thongke(item.id)}
                                                                </Typography>
                                                            </Box>
                                                        </Grid>
                                                    );
                                                })}
                                            </Grid>

                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* PANEL PHẢI: CAN THIỆP KẾT QUẢ VÀ NÚT RANDOM */}
                                <Grid item xs={12} lg={6}>
                                    <Card sx={{ height: '100%', border: '1px solid #E4E6EF', opacity: isLocked ? 0.7 : 1, transition: 'opacity 0.3s' }}>
                                        <CardContent sx={{ p: 0 }}>
                                            <Box sx={{ borderBottom: '1px solid #EFF2F5', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle1" fontWeight="800" color="#181C32" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PanToolOutlined color="primary" /> CHỌN BÀI PHIÊN #{formatId(bet?.id_bet) || '...'}
                                                </Typography>
                                                <Box display="flex" gap={1}>
                                                    <Button disabled={isLocked} variant="contained" size="small" onClick={() => handleRandomWin(1)} sx={{ backgroundColor: '#1B84FF', color: '#FFF', fontWeight: 800, '&:hover': { backgroundColor: '#005edb' } }}>RANDOM CON</Button>
                                                    <Button disabled={isLocked} variant="contained" size="small" onClick={() => handleRandomWin(2)} sx={{ backgroundColor: '#F1416C', color: '#FFF', fontWeight: 800, '&:hover': { backgroundColor: '#d1244d' } }}>RANDOM CÁI</Button>
                                                </Box>
                                            </Box>
                                            
                                            <Box p={4}>
                                                <form onSubmit={handleRealSubmit} style={{ width: '100%' }}>
                                                    
                                                    {/* KẾT QUẢ REAL-TIME */}
                                                    <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
                                                        <Typography variant="caption" fontWeight="800" color="#A1A5B7" mb={1} sx={{ letterSpacing: 1 }}>KẾT QUẢ TRÊN BÀN HIỆN TẠI</Typography>
                                                        <Box sx={{ 
                                                            backgroundColor: rtWinnerColor, 
                                                            color: '#FFF', 
                                                            fontWeight: 900, 
                                                            fontSize: '0.9rem', 
                                                            px: 3, 
                                                            py: 1.5, 
                                                            borderRadius: '8px', 
                                                            boxShadow: `0 4px 12px ${rtWinnerColor}40`,
                                                            minWidth: '160px',
                                                            textAlign: 'center',
                                                            transition: 'all 0.3s ease'
                                                        }}>
                                                            {rtWinnerTxt}
                                                        </Box>
                                                    </Box>

                                                    {/* CỘT CHỌN BÀI */}
                                                    <Grid container spacing={3} mb={3} alignItems="stretch">
                                                        {/* CON */}
                                                        <Grid item xs={6}>
                                                            <Box sx={{ backgroundColor: '#F1FAFF', p: 3, borderRadius: '12px', border: '1px dashed #93C5FD', height: '100%' }}>
                                                                <Typography variant="subtitle2" fontWeight="900" color="#1B84FF" mb={3} textAlign="center">
                                                                    BÊN CON ({currentPScore})
                                                                </Typography>
                                                                <CardSelectUI disabled={isLocked} label="Lá 1" card={pCard1} setCard={setPCard1} />
                                                                <CardSelectUI disabled={isLocked} label="Lá 2" card={pCard2} setCard={setPCard2} />
                                                                <CardSelectUI disabled={isLocked} label="Lá 3 (Tùy chọn)" card={pCard3} setCard={setPCard3} isOptional />
                                                            </Box>
                                                        </Grid>

                                                        {/* CÁI */}
                                                        <Grid item xs={6}>
                                                            <Box sx={{ backgroundColor: '#FFF5F8', p: 3, borderRadius: '12px', border: '1px dashed #FCA5A5', height: '100%' }}>
                                                                <Typography variant="subtitle2" fontWeight="900" color="#F1416C" mb={3} textAlign="center">
                                                                    BÊN CÁI ({currentBScore})
                                                                </Typography>
                                                                <CardSelectUI disabled={isLocked} label="Lá 1" card={bCard1} setCard={setBCard1} />
                                                                <CardSelectUI disabled={isLocked} label="Lá 2" card={bCard2} setCard={setBCard2} />
                                                                <CardSelectUI disabled={isLocked} label="Lá 3 (Tùy chọn)" card={bCard3} setCard={setBCard3} isOptional />
                                                            </Box>
                                                        </Grid>
                                                    </Grid>

                                                    <Button disabled={isLocked} type="submit" variant="contained" color="error" fullWidth sx={{ py: 1.5, fontWeight: 800, borderRadius: '8px', fontSize: '15px', mt: 1 }}>
                                                        {isLocked ? "ĐANG MỞ BÀI..." : "CHỐT VÁN NÀY"}
                                                    </Button>
                                                </form>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            {/* BẢNG DANH SÁCH NGƯỜI CHƠI ĐANG CƯỢC */}
                            <Card sx={{ border: '1px solid #E4E6EF', backgroundColor: '#FFFFFF', mb: 4 }}>
                                <Box sx={{ p: 2.5, borderBottom: '1px solid #EFF2F5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" fontWeight="800" color="#181C32" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <GroupOutlined color="primary" /> Người Chơi Đang Cược Phiên {formatId(bet?.id_bet)} (Bàn {currentTableId})
                                    </Typography>
                                    <Chip label={`Tổng: ${current ? current.length : 0} đơn`} size="small" sx={{ backgroundColor: '#F1FAFF', color: '#1B84FF', fontWeight: 800 }} />
                                </Box>
                                <TableContainer sx={{ overflowX: 'auto', maxHeight: '400px' }}>
                                    <Table stickyHeader sx={{ minWidth: 1460 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: '#A1A5B7 !important', fontWeight: '800 !important', textTransform: 'uppercase', backgroundColor: '#FFFFFF' }}>Tài Khoản</TableCell>
                                                <TableCell sx={{ color: '#A1A5B7 !important', fontWeight: '800 !important', textTransform: 'uppercase', backgroundColor: '#FFFFFF' }}>Cửa Cược</TableCell>
                                                <TableCell sx={{ color: '#A1A5B7 !important', fontWeight: '800 !important', textTransform: 'uppercase', backgroundColor: '#FFFFFF' }}>Số Tiền</TableCell>
                                                <TableCell sx={{ color: '#A1A5B7 !important', fontWeight: '800 !important', textTransform: 'uppercase', textAlign: 'right !important', backgroundColor: '#FFFFFF' }}>Thời Gian</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {current && current.length > 0 ? current.map((item, idx) => {
                                                const label = getBetLabel(item.state);
                                                const isCon = label.includes("CON");
                                                const isCai = label.includes("CÁI");

                                                let badgeColor = '#F1FAFF';
                                                let textColor = '#181C32';
                                                if (isCon) { badgeColor = '#F1FAFF'; textColor = '#1B84FF'; }
                                                else if (isCai) { badgeColor = '#FFF5F8'; textColor = '#F1416C'; }
                                                else { badgeColor = '#E8FFF3'; textColor = '#50CD89'; }

                                                return (
                                                    <TableRow key={idx} hover>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="800" color="#2563EB">{item?.user?.username || 'Ẩn danh'}</Typography>
                                                            <Typography variant="caption" fontWeight="600" color="text.secondary">ID: {item?.user?.iduser || 'N/A'}</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip label={label || item.state} size="small" sx={{ fontWeight: 800, borderRadius: '6px', px: 1, backgroundColor: badgeColor, color: textColor }} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight="800" color="#16A34A">
                                                                {Number(item.money).toLocaleString("vi-VN")} đ
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ textAlign: 'right !important', color: "text.secondary", fontSize: "0.8rem", fontWeight: "600" }}>
                                                            {formatDate(new Date(item.createdAt))}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }) : (
                                                <TableRow>
                                                    <TableCell colSpan={4} align="center" sx={{ py: 6, color: '#A1A5B7', fontWeight: 600 }}>
                                                        Chưa có người chơi nào cược trong kỳ này tại Sảnh {currentTableId}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Card>

                            {/* BẢNG LỊCH SỬ KẾT QUẢ TỪ API */}
                            <Card sx={{ border: '1px solid #E4E6EF', backgroundColor: '#FFFFFF' }}>
                                <Box sx={{ p: 2.5, borderBottom: '1px solid #EFF2F5' }}>
                                    <Typography variant="h6" fontWeight="800" color="#181C32">Lịch Sử Trận Đấu Bàn {currentTableId} (Gần nhất)</Typography>
                                </Box>
                                <Box sx={{ overflowX: 'auto' }}>
                                    <Table sx={{ minWidth: 1460 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ color: '#A1A5B7 !important', fontWeight: '800 !important', textTransform: 'uppercase', width: '150px' }}>Kỳ quay</TableCell>
                                                <TableCell sx={{ color: '#A1A5B7 !important', fontWeight: '800 !important', textTransform: 'uppercase' }}>Kết quả Trận Đấu</TableCell>
                                                <TableCell sx={{ color: '#A1A5B7 !important', fontWeight: '800 !important', textTransform: 'uppercase', textAlign: 'right !important', width: '150px' }}>Thời gian</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {list30 && list30.length > 0 ? list30
                                                .filter((item) => !bet || !bet.id_bet ? true : Number(formatId(item.id_bet)) <= Number(formatId(bet.id_bet)))
                                                .sort((a, b) => Number(formatId(b.id_bet)) - Number(formatId(a.id_bet)))
                                                .slice(0, 30) 
                                                .map((item, idx) => {
                                                    const isCurrent = item.id_bet === bet?.id_bet;
                                                    if (!isCurrent && !item.result) return null;

                                                    return (
                                                        <TableRow key={item._id || idx} sx={{ backgroundColor: isCurrent ? '#FFF8DD' : 'inherit', transition: 'background-color 0.3s' }}>
                                                            <TableCell>
                                                                <Typography variant="body2" fontWeight={isCurrent ? 800 : 700} color={isCurrent ? '#FFC700' : '#181C32'}>
                                                                    #{formatId(item?.id_bet)} {isCurrent && <span style={{fontSize: '11px', color: '#F1416C', display: 'block'}}>Đang cược...</span>}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {isCurrent && !item.result ? (
                                                                    <Typography variant="body2" fontWeight="700" color="text.secondary" fontStyle="italic">
                                                                        Đang chờ xử lý...
                                                                    </Typography>
                                                                ) : (
                                                                    renderCardHistory(item.result)
                                                                )}
                                                            </TableCell>
                                                            <TableCell sx={{ textAlign: 'right !important' }}>
                                                                <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                                    {formatDate(new Date(item.createdAt))}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            : null}
                                        </TableBody>
                                    </Table>
                                </Box>
                            </Card>

                        </Container>
                    </Box>
                </ThemeProvider>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default SetBcr;